import { Clock, systemClock } from './issue-pending-writes';

// Several tabs share one IndexedDB per tenant. If each polled and wrote, both would
// advance the same cursor and each would miss the pages the other consumed — the
// rows are gone from neither tab's perspective and no error is raised anywhere. One
// tab writes; the rest read what it applied.
//
// Election runs over BroadcastChannel with a heartbeat lease. A lease matters more
// than an election: a tab that crashes or is killed cannot resign, so leadership has
// to expire on its own rather than be handed over.
//
// The channel name carries the tenant, so tabs viewing different tenants never
// coordinate and a switch lands on a different channel as well as a different
// database.

export const TabRoles = { Leader: 'leader', Follower: 'follower' } as const;
export type TabRole = typeof TabRoles[keyof typeof TabRoles];

type Message =
  | { kind: 'heartbeat'; tabId: string }
  | { kind: 'claim'; tabId: string }
  | { kind: 'resign'; tabId: string }
  | { kind: 'applied'; tabId: string; scopeKey: string; rowIds: string[]; removedIds: string[] }
  | { kind: 'confirm'; tabId: string; issueIds: string[] };

// What the writing tab applied. Followers need the ids, not just the scope: a write
// made in a follower tab is held in that tab's own shadow, and only the writer applies
// deltas. Without the ids the follower would never see its own write confirmed and
// would roll it back as failed even though it landed.
export interface AppliedNotice {
  scopeKey: string;
  rowIds: string[];
  removedIds: string[];
}

// Leadership expires after this long without a heartbeat. Two ticks of headroom, so
// one delayed tick on a busy tab does not trigger a needless handover.
const LEASE_MS = 9_000;
const HEARTBEAT_MS = 3_000;

export function issueSyncChannelName(externalTenantId: string): string {
  return `fleethd-issues-sync-${externalTenantId || 'anon'}`;
}

// A channel, as a port. Tests substitute an in-memory pair to run two tabs in one
// process, and a browser without BroadcastChannel falls back to single-tab mode.
export interface SyncChannel {
  post(message: unknown): void;
  listen(handler: (message: unknown) => void): void;
  close(): void;
}

export function openSyncChannel(name: string): SyncChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  let channel: BroadcastChannel;
  try {
    channel = new BroadcastChannel(name);
  } catch {
    return null;
  }
  return {
    post: message => { try { channel.postMessage(message); } catch { /* closed */ } },
    listen: handler => { channel.onmessage = event => handler(event.data); },
    close: () => { try { channel.close(); } catch { /* already closed */ } },
  };
}

export class IssueSyncTabs {
  readonly tabId: string;

  private role: TabRole = TabRoles.Follower;
  private claiming = false;
  private leaderSeenAtMs: number;
  private lastHeartbeatSentMs = 0;
  // The lowest competing id seen since this tab staked a claim. Ties resolve by
  // comparing ids, so two tabs claiming in the same instant still yield one leader.
  private rivalClaim = '';
  // Whether a heartbeat has arrived since this tab staked its claim. A flag rather
  // than a timestamp comparison: a leader can answer a claim within the same
  // millisecond, and comparing times would read that answer as not having happened.
  private heartbeatSinceClaim = false;
  private appliedHandler: (applied: AppliedNotice) => void = () => undefined;
  private confirmHandler: (issueIds: string[]) => void = () => undefined;

  constructor(
    private readonly channel: SyncChannel | null,
    private readonly clock: Clock = systemClock,
    tabId?: string,
  ) {
    this.tabId = tabId ?? `${this.clock.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    // With no channel this tab is alone by definition and must write, or nothing
    // would sync at all.
    this.role = this.channel ? TabRoles.Follower : TabRoles.Leader;
    this.leaderSeenAtMs = this.clock.now();
    this.channel?.listen(message => this.receive(message as Message));
  }

  // Whether leadership can be handed to another tab. Without a channel this tab is alone
  // and holds the role unconditionally — `tick()` cannot claim, so a resignation here would
  // be permanent and nothing would ever sync again.
  canHandOff(): boolean {
    return !!this.channel;
  }

  isLeader(): boolean {
    return this.role === TabRoles.Leader;
  }

  onApplied(handler: (applied: AppliedNotice) => void): void {
    this.appliedHandler = handler;
  }

  // Raised on the writing tab when another tab asks for its own write to be confirmed.
  onConfirmRequested(handler: (issueIds: string[]) => void): void {
    this.confirmHandler = handler;
  }

  // Advances the lease. Called on a timer in the app and directly in a test. A claim
  // is resolved on the tick after it is staked rather than after an internal delay,
  // which keeps the whole election deterministic and free of wall-clock waits.
  tick(): void {
    if (!this.channel) return;
    const now = this.clock.now();

    if (this.role === TabRoles.Leader) {
      if (now - this.lastHeartbeatSentMs >= HEARTBEAT_MS) {
        this.lastHeartbeatSentMs = now;
        this.channel.post({ kind: 'heartbeat', tabId: this.tabId } satisfies Message);
      }
      return;
    }

    if (this.claiming) {
      // A heartbeat since the claim means a leader is alive and this tab yields. A
      // rival with a lower id wins the tie.
      const beaten = this.heartbeatSinceClaim
        || (!!this.rivalClaim && this.rivalClaim < this.tabId);
      this.claiming = false;
      this.rivalClaim = '';
      if (beaten) {
        this.leaderSeenAtMs = now;
        return;
      }
      this.becomeLeader();
      return;
    }

    if (now - this.leaderSeenAtMs >= LEASE_MS) {
      this.claiming = true;
      this.rivalClaim = '';
      this.heartbeatSinceClaim = false;
      this.channel.post({ kind: 'claim', tabId: this.tabId } satisfies Message);
    }
  }

  // Tells other tabs a scope changed on disk so they can re-read it instead of
  // fetching the same delta again, and which rows moved so they can settle their own
  // pending writes.
  announceApplied(scopeKey: string, rowIds: string[] = [], removedIds: string[] = []): void {
    this.channel?.post(
      { kind: 'applied', tabId: this.tabId, scopeKey, rowIds, removedIds } satisfies Message,
    );
  }

  // Asks the writing tab to confirm a write this tab made. Only one tab fetches, so a
  // follower cannot confirm its own write and would otherwise wait for the ordinary
  // poll — far longer than the write deadline allows.
  requestConfirm(issueIds: string[]): void {
    this.channel?.post({ kind: 'confirm', tabId: this.tabId, issueIds } satisfies Message);
  }

  // Gives up leadership deliberately, on teardown or a tenant switch, so a surviving
  // tab takes over immediately rather than waiting out the lease.
  resign(): void {
    if (this.role === TabRoles.Leader) {
      this.channel?.post({ kind: 'resign', tabId: this.tabId } satisfies Message);
    }
    this.role = TabRoles.Follower;
    this.claiming = false;
    this.leaderSeenAtMs = this.clock.now();
  }

  close(): void {
    this.resign();
    this.channel?.close();
  }

  private becomeLeader(): void {
    this.role = TabRoles.Leader;
    this.lastHeartbeatSentMs = this.clock.now();
    this.channel?.post({ kind: 'heartbeat', tabId: this.tabId } satisfies Message);
  }

  private receive(message: Message): void {
    if (!message || message.tabId === this.tabId) return;

    switch (message.kind) {
      case 'heartbeat':
        this.leaderSeenAtMs = this.clock.now();
        this.heartbeatSinceClaim = true;
        // Two leaders can coexist for a moment after a partition heals. The lower id
        // keeps it so the pair converges instead of trading leadership.
        if (this.role === TabRoles.Leader && message.tabId < this.tabId) {
          this.role = TabRoles.Follower;
          this.claiming = false;
        }
        break;

      case 'claim':
        if (this.role === TabRoles.Leader) {
          // Assert leadership at once so the claimant yields on its next tick.
          this.lastHeartbeatSentMs = this.clock.now();
          this.channel?.post({ kind: 'heartbeat', tabId: this.tabId } satisfies Message);
          break;
        }
        if (this.claiming && (!this.rivalClaim || message.tabId < this.rivalClaim)) {
          this.rivalClaim = message.tabId;
        }
        break;

      case 'resign':
        // Expire the lease immediately so the next tick starts a claim.
        this.leaderSeenAtMs = this.clock.now() - LEASE_MS;
        break;

      case 'applied':
        this.appliedHandler({
          scopeKey: message.scopeKey,
          rowIds: message.rowIds ?? [],
          removedIds: message.removedIds ?? [],
        });
        break;

      case 'confirm':
        if (this.role === TabRoles.Leader) this.confirmHandler(message.issueIds ?? []);
        break;
    }
  }
}
